import React from 'react';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { wrappedTranslations } from '../../../translations/wrapped';

interface MonthlyBreakdownCardProps {
    monthlyData: Array<{
        month: string;
        games: number;
        wins: number;
    }>;
    lang: 'en' | 'fr' | 'de' | 'it';
}

const MonthlyBreakdownCard: React.FC<MonthlyBreakdownCardProps> = ({ monthlyData, lang }) => {
    const t = wrappedTranslations[lang];
    // Find best month
    const bestMonth = [...monthlyData].sort((a, b) => b.wins - a.wins)[0];

    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className="max-w-6xl w-full flex flex-col items-center">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                >
                    <Calendar className="w-20 h-20 md:w-24 md:h-24 mb-8 mx-auto text-[#1C7AD6]" />
                </motion.div>

                <motion.h2
                    className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 tracking-tight"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    {t.yourYearInReview}
                </motion.h2>

                {bestMonth && (
                    <motion.p
                        className="text-base md:text-lg text-gray-600 mb-10"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                    >
                        <span className="text-[#1C7AD6] font-bold">{bestMonth.month}</span> {t.busiestMonth} <span className="text-gray-900 font-bold">{bestMonth.games}</span> {t.withMatches}
                    </motion.p>
                )}

                <motion.div
                    className="w-full h-64 md:h-96 bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-lg"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthlyData}>
                            <XAxis
                                dataKey="month"
                                stroke="#6b7280"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                                dy={10}
                            />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', color: '#111827', padding: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                cursor={{ fill: 'rgba(28, 122, 214, 0.05)' }}
                            />
                            <Bar dataKey="games" fill="#e5e7eb" radius={[6, 6, 0, 0]} />
                            <Bar dataKey="wins" fill="#1C7AD6" radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </motion.div>

                <motion.div
                    className="flex gap-8 mt-8 text-sm md:text-base text-gray-600"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                >
                    <div className="flex items-center gap-3">
                        <div className="w-4 h-4 bg-gray-300 rounded-md"></div>
                        <span>{t.matchesPlayedLegend}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-4 h-4 bg-[#1C7AD6] rounded-md"></div>
                        <span>{t.matchesWonLegend}</span>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default MonthlyBreakdownCard;
